// Live remittance comparison widget — affiliate revenue core component
// Shows real-time USD/KRW rates with fee breakdown per provider.
// Affiliate links: Wise (partners.wise.com) + Remitly (impact.com) — register before launch.

import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp, TrendingDown, Minus, Bell, BellOff,
  ExternalLink, AlertCircle, CheckCircle, Gift, Clock, Info
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import Skeleton from '@/components/common/Skeleton';

const API_URL = import.meta.env.VITE_API_URL || '';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProviderResult {
  id: string;
  displayName: string;
  isEstimate: boolean;
  transferTime: string;
  affiliateUrl: string;
  referralBonus: string | null;
  fee: number;
  rate: number;
  youSend: number;
  theyReceive: number;
}

interface RatesResponse {
  fromCurrency: string;
  toCurrency: string;
  amount: number;
  rate: number;
  stale: boolean;
  staleMinutes: number | null;
  avgRate7d: number | null;
  trend: 'good' | 'weak' | 'neutral';
  trendPct: number;
  providers: ProviderResult[];
  updatedAt: string;
}

interface Alert {
  id: string;
  targetRate: number;
  direction: 'above' | 'below';
  isActive: boolean;
}

// ---------------------------------------------------------------------------
// Provider logos (initials-based styled avatars per Obangsaek palette)
// ---------------------------------------------------------------------------
const PROVIDER_COLORS: Record<string, { bg: string; text: string; char: string }> = {
  wise: { bg: 'bg-[#00B9FF]', text: 'text-white', char: 'W' },
  remitly: { bg: 'bg-coral-500', text: 'text-white', char: 'R' },
  woori: { bg: 'bg-[#0047AB]', text: 'text-white', char: '우' },
  hana: { bg: 'bg-teal-600', text: 'text-white', char: '하' },
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ProviderAvatar({ providerId }: { providerId: string }) {
  const cfg = PROVIDER_COLORS[providerId] ?? { bg: 'bg-gray-400', text: 'text-white', char: '?' };
  return (
    <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full font-bold text-sm ${cfg.bg} ${cfg.text} flex-shrink-0`}>
      {cfg.char}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Inline SVG sparkline — no chart library, pure SVG path
// ---------------------------------------------------------------------------

interface SparkPoint { date: string; rate: number }

function RateSparkline({ points, trend }: { points: SparkPoint[]; trend: 'good' | 'weak' | 'neutral' }) {
  if (points.length < 2) return null;

  const W = 200;
  const H = 36;
  const PAD = 4;

  const rates = points.map(p => p.rate);
  const min = Math.min(...rates);
  const max = Math.max(...rates);
  const range = max - min || 1;

  const x = (i: number) => PAD + (i / (points.length - 1)) * (W - PAD * 2);
  const y = (r: number) => H - PAD - ((r - min) / range) * (H - PAD * 2);

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.rate).toFixed(1)}`).join(' ');

  const color = trend === 'good' ? '#16a34a' : trend === 'weak' ? '#dc2626' : '#6b7280';
  const fillColor = trend === 'good' ? '#dcfce7' : trend === 'weak' ? '#fee2e2' : '#f3f4f6';

  // Close path for fill area
  const fillD = `${pathD} L${x(points.length - 1).toFixed(1)},${H} L${PAD},${H} Z`;

  const first = points[0];
  const last = points[points.length - 1];
  const pct = ((last.rate - first.rate) / first.rate * 100);
  const sign = pct >= 0 ? '+' : '';

  return (
    <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
      <div className="flex-shrink-0">
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="overflow-visible">
          {/* Fill */}
          <path d={fillD} fill={fillColor} opacity="0.5" />
          {/* Line */}
          <path d={pathD} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          {/* First dot */}
          <circle cx={x(0)} cy={y(first.rate)} r="2.5" fill={color} />
          {/* Last dot */}
          <circle cx={x(points.length - 1)} cy={y(last.rate)} r="3" fill={color} stroke="white" strokeWidth="1.5" />
        </svg>
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 dark:text-gray-400">7-day trend</p>
        <p className="text-xs font-semibold" style={{ color }}>
          {sign}{pct.toFixed(2)}%
          <span className="text-gray-400 dark:text-gray-500 font-normal ml-1">
            ({points.length} days)
          </span>
        </p>
      </div>
    </div>
  );
}

function TrendBadge({ trend, trendPct }: { trend: 'good' | 'weak' | 'neutral'; trendPct: number }) {
  if (trend === 'good') {
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
        <TrendingUp className="w-3.5 h-3.5" />
        Rate is good today +{trendPct.toFixed(2)}% vs 7-day avg
      </span>
    );
  }
  if (trend === 'weak') {
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
        <TrendingDown className="w-3.5 h-3.5" />
        Rate is weak today {trendPct.toFixed(2)}% vs 7-day avg
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
      <Minus className="w-3.5 h-3.5" />
      Rate is average today
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function RemittanceWidget() {
  const { user, token } = useAuthStore();
  const [amount, setAmount] = useState('1000');
  const [alertTarget, setAlertTarget] = useState('');
  const [alertDirection, setAlertDirection] = useState<'above' | 'below'>('above');
  const [alertProviderId, setAlertProviderId] = useState<string | null>(null);
  const [alertSuccess, setAlertSuccess] = useState<string | null>(null);
  const [alertError, setAlertError] = useState<string | null>(null);
  const [isSettingAlert, setIsSettingAlert] = useState(false);

  // Auto-dismiss success toast after 5 seconds
  useEffect(() => {
    if (!alertSuccess) return;
    const t = setTimeout(() => setAlertSuccess(null), 5000);
    return () => clearTimeout(t);
  }, [alertSuccess]);

  const parsedAmount = parseFloat(amount) || 1000;
  const safeAmount = Math.max(1, Math.min(parsedAmount, 1_000_000));

  const { data, isLoading, isError } = useQuery<RatesResponse>({
    queryKey: ['remittance-rates', safeAmount],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/remittance/rates?from=USD&to=KRW&amount=${safeAmount}`);
      if (!res.ok) throw new Error('Failed to fetch rates');
      return res.json();
    },
    staleTime: 3 * 60 * 1000, // 3 minutes
    refetchInterval: 3 * 60 * 1000,
  });

  // 7-day rate history for sparkline
  const { data: historyData } = useQuery<{ history: SparkPoint[] }>({
    queryKey: ['remittance-history'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/remittance/history`);
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    staleTime: 60 * 60 * 1000, // 1 hour — history changes once per day
  });
  const historyPoints = historyData?.history ?? [];

  // Fetch user's existing alerts (only when authenticated)
  const { data: alertsData, refetch: refetchAlerts } = useQuery<{ alerts: Alert[] }>({
    queryKey: ['remittance-alerts'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/remittance/alerts`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch alerts');
      return res.json();
    },
    enabled: !!user && !!token,
    staleTime: 60 * 1000,
  });
  const existingAlerts = alertsData?.alerts ?? [];

  const handleAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^0-9.]/g, '');
    setAmount(val);
  }, []);

  const handleProviderClick = useCallback(async (providerId: string) => {
    // Fire-and-forget click tracking
    fetch(`${API_URL}/api/remittance/click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ providerId }),
    }).catch(() => {});
  }, []);

  const handleSetAlert = useCallback(async () => {
    if (!user || !token) {
      setAlertError('Sign in to set rate alerts');
      return;
    }
    if (!alertTarget || parseFloat(alertTarget) <= 0) {
      setAlertError('Enter a valid target rate');
      return;
    }

    setIsSettingAlert(true);
    setAlertError(null);
    try {
      const res = await fetch(`${API_URL}/api/remittance/alerts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          targetRate: parseFloat(alertTarget),
          direction: alertDirection,
          fromCurrency: 'USD',
          toCurrency: 'KRW',
        }),
      });

      if (!res.ok) {
        const err = await res.json() as { error: string };
        throw new Error(err.error || 'Failed to create alert');
      }

      setAlertSuccess(`Alert set: notify me when rate goes ${alertDirection} ₩${parseInt(alertTarget).toLocaleString()}`);
      setAlertProviderId(null);
      setAlertTarget('');
      refetchAlerts();
    } catch (err: unknown) {
      setAlertError(err instanceof Error ? err.message : 'Failed to set alert');
    } finally {
      setIsSettingAlert(false);
    }
  }, [user, token, alertTarget, alertDirection, refetchAlerts]);

  const handleDeleteAlert = useCallback(async (alertId: string) => {
    if (!token) return;
    try {
      await fetch(`${API_URL}/api/remittance/alerts/${alertId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      refetchAlerts();
    } catch {
      // silent — the list will still show the alert; user can retry
    }
  }, [token, refetchAlerts]);

  const formatKRW = (n: number) => `₩${n.toLocaleString()}`;
  const formatUSD = (n: number) => `$${n.toFixed(2)}`;

  // -------------------------------------------------------------------------
  // Loading state
  // -------------------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
        <AlertCircle className="w-5 h-5 flex-shrink-0" />
        <p className="text-sm">Couldn't load live rates. <button onClick={() => window.location.reload()} className="underline">Try again</button></p>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="space-y-6">
      {/* Stale banner */}
      {data.stale && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          Showing cached rates — live data temporarily unavailable
        </div>
      )}

      {/* Calculator */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          How much are you sending?
        </label>
        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold text-gray-500 dark:text-gray-400">$</span>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={handleAmountChange}
            placeholder="1000"
            className="flex-1 text-2xl font-bold bg-transparent border-b-2 border-[#0047AB] dark:border-blue-400 outline-none pb-1 text-gray-900 dark:text-white"
            aria-label="Amount in USD"
          />
          <span className="text-sm font-medium text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">USD → KRW</span>
        </div>

        {/* Live rate + trend */}
        <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Mid-market: <span className="font-semibold text-gray-800 dark:text-gray-200">{formatKRW(Math.round(data.rate))} / $1</span>
          </span>
          {data.avgRate7d && (
            <TrendBadge trend={data.trend} trendPct={data.trendPct} />
          )}
        </div>

        {/* 7-day sparkline — hidden until 2+ days of history exist */}
        <RateSparkline points={historyPoints} trend={data.trend} />
      </div>

      {/* Alert success toast */}
      {alertSuccess && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          {alertSuccess}
          <button onClick={() => setAlertSuccess(null)} className="ml-auto text-green-600 hover:text-green-800 text-xs underline">dismiss</button>
        </div>
      )}

      {/* Provider cards */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            Compare providers{data.providers.length > 0 ? ` — ${data.providers.length} options` : ''}
          </h3>
          <span className="text-xs text-gray-400">
            Source updated {new Date(data.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>

        {data.providers.length === 0 && (
          <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <div className="flex-1 text-sm">
              <p className="font-medium">No providers available right now</p>
              <p className="text-amber-700 text-xs mt-0.5">Rate data may be temporarily unavailable. <button onClick={() => window.location.reload()} className="underline">Refresh</button></p>
            </div>
          </div>
        )}

        {data.providers.map((p, idx) => (
          <div
            key={p.id}
            className={`bg-white dark:bg-gray-800 rounded-xl border shadow-sm overflow-hidden ${
              idx === 0
                ? 'border-[#0047AB] dark:border-blue-500 ring-1 ring-[#0047AB]/20'
                : 'border-gray-200 dark:border-gray-700'
            }`}
          >
            {idx === 0 && (
              <div className="bg-[#0047AB] text-white text-xs font-medium px-4 py-1 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Highest recipient total shown
              </div>
            )}

            <div className="p-4">
              {/* Provider header */}
              <div className="flex items-center gap-3 mb-3">
                <ProviderAvatar providerId={p.id} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 dark:text-white">{p.displayName}</span>
                    {p.isEstimate && (
                      <span className="text-xs text-gray-400 flex items-center gap-0.5">
                        <Info className="w-3 h-3" /> est.
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    <Clock className="w-3 h-3" />
                    {p.transferTime}
                  </div>
                </div>
                {p.referralBonus && (
                  <span className="hidden sm:flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                    <Gift className="w-3 h-3" />
                    {p.referralBonus}
                  </span>
                )}
              </div>

              {/* Mobile referral bonus */}
              {p.referralBonus && (
                <div className="sm:hidden flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-1 mb-3 w-fit">
                  <Gift className="w-3 h-3" />
                  {p.referralBonus}
                </div>
              )}

              {/* Fee breakdown */}
              <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">You send</div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm">{formatUSD(p.youSend)}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Fee</div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm">
                    {p.fee === 0 ? <span className="text-green-600">Free</span> : formatUSD(p.fee)}
                  </div>
                </div>
                <div className={`rounded-lg p-2 ${idx === 0 ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-gray-50 dark:bg-gray-700/50'}`}>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">They get</div>
                  <div className={`font-bold text-sm ${idx === 0 ? 'text-[#0047AB] dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                    {formatKRW(p.theyReceive)}
                  </div>
                </div>
              </div>

              {/* Rate line */}
              <div className="text-xs text-gray-400 dark:text-gray-500 mb-3">
                Rate: {formatKRW(Math.round(p.rate))} / $1
                {p.isEstimate && ' · Estimated fees — verify on provider site'}
              </div>

              {/* CTA row */}
              <div className="flex items-center gap-2">
                <a
                  href={p.affiliateUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleProviderClick(p.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                    idx === 0
                      ? 'bg-[#0047AB] text-white hover:bg-[#003d9e]'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Send with {p.displayName}
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>

                {/* Alert toggle */}
                <button
                  onClick={() => {
                    setAlertProviderId(alertProviderId === p.id ? null : p.id);
                    setAlertError(null);
                  }}
                  className="p-2.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 hover:text-[#0047AB] hover:border-[#0047AB] transition-colors"
                  title="Set rate alert"
                  aria-label="Set rate alert"
                >
                  {alertProviderId === p.id ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                </button>
              </div>

              {/* Inline alert form */}
              {alertProviderId === p.id && (
                <div className="mt-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <p className="text-xs font-medium text-blue-900 dark:text-blue-200 mb-2">
                    Notify me when USD/KRW rate is:
                  </p>
                  <div className="flex items-center gap-2 mb-2">
                    <select
                      value={alertDirection}
                      onChange={(e) => setAlertDirection(e.target.value as 'above' | 'below')}
                      className="text-sm border border-blue-300 rounded px-2 py-1.5 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="above">↑ above</option>
                      <option value="below">↓ below</option>
                    </select>
                    <div className="flex items-center flex-1 border border-blue-300 rounded bg-white dark:bg-gray-700 px-2">
                      <span className="text-gray-500 text-sm">₩</span>
                      <input
                        type="number"
                        value={alertTarget}
                        onChange={(e) => setAlertTarget(e.target.value)}
                        placeholder={Math.round(data.rate).toString()}
                        className="flex-1 text-sm py-1.5 pl-1 outline-none bg-transparent dark:text-white"
                        aria-label="Target rate in KRW"
                      />
                    </div>
                  </div>
                  {alertError && (
                    <p className="text-xs text-red-600 mb-2">{alertError}</p>
                  )}
                  {!user && (
                    <p className="text-xs text-blue-700 dark:text-blue-300 mb-2">
                      <a href="/login" className="underline font-medium">Sign in</a> to save rate alerts
                    </p>
                  )}
                  <button
                    onClick={handleSetAlert}
                    disabled={isSettingAlert || !alertTarget}
                    className="w-full py-1.5 rounded text-sm font-medium bg-[#0047AB] text-white hover:bg-[#003d9e] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSettingAlert ? 'Saving…' : 'Set alert'}
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Active rate alerts — shown only when logged in and alerts exist */}
      {user && existingAlerts.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-[#0047AB]" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Your rate alerts
              </h3>
            </div>
            <span className="text-xs text-gray-400">{existingAlerts.filter(a => a.isActive).length}/5 active</span>
          </div>
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {existingAlerts.map((alert) => (
              <li key={alert.id} className="flex items-center justify-between px-4 py-2.5 gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <Bell className={`w-3.5 h-3.5 flex-shrink-0 ${alert.isActive ? 'text-[#0047AB]' : 'text-gray-300'}`} />
                  <span>
                    {alert.direction === 'above' ? '↑ above' : '↓ below'}{' '}
                    <span className="font-semibold">{formatKRW(alert.targetRate)}</span>
                    {!alert.isActive && <span className="ml-1 text-xs text-gray-400">(fired)</span>}
                  </span>
                </div>
                <button
                  onClick={() => handleDeleteAlert(alert.id)}
                  className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                  aria-label="Delete alert"
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Disclaimer */}
      <p className="text-xs text-gray-400 dark:text-gray-500 text-center leading-relaxed">
        * "Est." fees are approximate and may vary. Live rates from Wise API. KoreanHub earns affiliate commissions when you sign up via our links — at no cost to you.
      </p>
    </div>
  );
}
